import React from 'react';
import { useTranslation } from 'react-i18next';
import { QuoteJob } from '../../types/quote';

import { formatDateLocalized } from '../../utils/dateUtils';
import { Job } from '../../types/job';
import ProfileAvatar from '../common/ProfileAvatar';

interface QuoteJobInfoProps {
  quoteJob: QuoteJob | Job;
  headerOnly?: boolean;
  detailsOnly?: boolean;
  showProfileAvatar?: boolean;
  statusLabel?: string;
  ownershipStatusLabel?: string | null;
}

export const QuoteJobInfo: React.FC<QuoteJobInfoProps> = ({
  quoteJob,
  headerOnly = false,
  detailsOnly = false,
  showProfileAvatar = true,
  statusLabel,
  ownershipStatusLabel,
}) => {
  const { t, i18n } = useTranslation(['quotes', 'common']);

  const formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const publishedDateFormatted = quoteJob.published_at
    ? formatDateLocalized(quoteJob.published_at, i18n.language, formatOptions)
    : '-';
  const startDate = quoteJob.start_date
    ? formatDateLocalized(quoteJob.start_date, i18n.language, formatOptions)
    : '-';
  const dueDate = quoteJob.end_date
    ? formatDateLocalized(quoteJob.end_date, i18n.language, formatOptions)
    : '-';

  const companyName = quoteJob.client.company_name || quoteJob.client.first_name;
  const projectName = quoteJob.title;
  const description = quoteJob.description;
  const cityName = quoteJob.city.name;
  const onSite = quoteJob.on_site ? t('common:form_fields.yes') : t('common:form_fields.no');
  const priceRange = t(`common:budget_ranges.${quoteJob.budget_range.code}`, {
    defaultValue: quoteJob.budget_range.code,
  });

  const projectType =
    quoteJob.required_services.length > 0
      ? quoteJob.required_services.map(service => service.label).join(', ')
      : t('general');

  return (
    <div className="space-y-6 mt-4">
      {!detailsOnly && (
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
          <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-12">
            <div className="flex min-w-0 flex-col gap-1 md:col-span-5">
              <div className="truncate text-xl font-bold text-secondary">{projectName}</div>
              <div className="break-words text-sm text-secondary">{projectType}</div>
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-3 md:col-span-7">
              <div className="flex min-w-0 flex-col">
                <span className="text-xs tracking-wider">
                  {t('quoteRequestInfo.publishedDate')}
                </span>
                <span className="break-words font-bold text-secondary">
                  {publishedDateFormatted}
                </span>
              </div>

              <div className="min-w-0 text-center">
                {ownershipStatusLabel && (
                  <span className="inline-block w-full break-words rounded-xl bg-[#E1E9F0] px-3 py-1 text-xs text-secondary">
                    {ownershipStatusLabel}
                  </span>
                )}
              </div>

              <div className="min-w-0 text-center">
                {statusLabel && (
                  <span className="inline-block w-full break-words rounded-xl bg-[#E1E9F0] px-3 py-1 text-xs text-secondary">
                    {statusLabel}
                  </span>
                )}
              </div>
            </div>
          </div>

          {showProfileAvatar && (
            <div className="flex items-start justify-end">
              <ProfileAvatar
                profile_id={quoteJob.client.id}
                first_name={quoteJob.client.first_name}
                avatar_url={quoteJob.client.avatar_url}
                className="h-10 w-10"
              />
            </div>
          )}
        </div>
      )}
      {!headerOnly && (
        <div className="pt-4">
          <h3 className="mb-4 text-lg font-bold text-secondary">
            {t('quoteRequestInfo.projectInfo')}
          </h3>
          <div className="grid grid-cols-1 items-start gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {[
              { label: t('quoteRequestInfo.companyName'), value: companyName },
              { label: t('quoteRequestInfo.priceRange'), value: priceRange },
              { label: t('quoteRequestInfo.startDate'), value: startDate },
              {
                label: t('quoteRequestInfo.dueDate'),
                value: dueDate,
                condition: !!quoteJob.end_date,
              },
              { label: t('quoteRequestInfo.onsite'), value: onSite },
              { label: t('quoteRequestInfo.city'), value: cityName, condition: quoteJob.on_site },
            ].map(
              (item, index) =>
                item.condition !== false && (
                  <div key={index} className="flex min-w-0 flex-col">
                    <span className="mb-1 text-xs uppercase text-gray-500">{item.label}</span>
                    <span className="break-words text-sm font-bold leading-tight text-secondary">
                      {item.value}
                    </span>
                  </div>
                )
            )}
          </div>

          <div className="mt-6 border-t border-gray-100 pt-4">
            <h3 className="mb-2 text-lg font-bold text-secondary">
              {t('quoteRequestInfo.jobDescription')}
            </h3>
            <p className="whitespace-pre-wrap leading-relaxed text-secondary opacity-90">
              {description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
